import * as THREE from "three";
import { getMap } from "../levels/maps/index.js";
import { DifficultyManager } from "../utils/DifficultyManager.js";
import {
  deriveTemplateGeometry,
  computeDy,
  isEligible,
  JUMP_HEADROOM,
  H_TARGET,
} from "../levels/templateGeometry.js";

export class LevelGenerator {
  constructor(
    scene,
    blockManager,
    obstacleManager,
    laserManager,
    debugManager,
    projectileManager,
    mapId = null
  ) {
    this.scene = scene;
    this.blockManager = blockManager;
    this.obstacleManager = obstacleManager;
    this.laserManager = laserManager;
    this.debugManager = debugManager;
    this.projectileManager = projectileManager;

    // Resolve which map (template bundle) we're playing. Unknown/missing ids
    // fall back to the default ("classic"), so a bad ?map= never breaks boot.
    this.map = getMap(mapId);

    // "designed" maps are finite, hand-built courses (an ordered `sequence` of
    // scenes ending in a finish) instead of an endless difficulty mix — see
    // _helpers.js and docs/level-generation.md.
    this.isDesigned = this.map.type === "designed";

    // "absolute" placement (custom continuous levels): render every scene at its
    // authored bottom-anchored position (dy=0) instead of chaining entry floors
    // to the running height. This reproduces a freely-painted canvas exactly —
    // floor steps and pits that land on a 30-col seam are NOT re-aligned. Built-in
    // designed/procedural maps stay "chained" (the default).
    this.absolutePlacement = this.map.placement === "absolute";

    if (this.map.type !== "procedural" && !this.isDesigned) {
      console.warn(
        `LevelGenerator: map "${this.map.id}" has unknown type "${this.map.type}"; ` +
          `treating as procedural.`
      );
    }
    console.log(
      `LevelGenerator: using map "${this.map.id}" (${this.map.name}) [${this.map.type}]`
    );

    // Designed-course playback state (unused by procedural maps):
    //  - sequenceIndex: how far we've walked the authored scene order.
    //  - startX0:       world X where sequence scene 0 was placed (set in reset).
    //  - courseEndX:    world X of the finish line (right edge of the finish scene).
    //  - finishMesh:    the finish-line marker (a Three.js mesh) we own/dispose.
    this.sequenceIndex = 0;
    this.startX0 = 0;
    this.courseEndX = null;
    this.finishMesh = null;

    // Grid dimensions for each scene
    this.sceneWidth = 30; // 30 grid cells wide
    this.sceneHeight = 20; // 20 grid cells high

    // Size of each grid cell in world units
    // 0.5 units = 50px at 1000px effective height (when frustumHeight is 10)
    this.gridCellSize = 0.5;

    // Track current and next scenes
    this.currentSceneX = 0;
    this.generatedScenes = [];

    // Cumulative floor height (in grid cells, 0 = base) that the next scene must
    // start at, so sections chain into one continuous surface. Set in reset().
    this.currentHeight = 0;

    // Base ground level - bottom of the screen
    this.baseGroundY = -5.0; // Base level for ground blocks (lowered to match BlockManager)

    // Initialize the difficulty manager (the map may override the progression;
    // by default every map shares the same distance-based ramp).
    this.difficultyManager = new DifficultyManager(this.map.difficulty);

    // Load templates (parses grids + derives placement geometry per template)
    this.templates = this.loadTemplates();

    // Current player distance
    this.playerDistance = 0;

    // Populate template dropdown in debug manager if available
    if (this.debugManager) {
      this.debugManager.populateTemplateDropdown(this.templates);
    }
  }

  loadTemplates() {
    // Build one lookup of name -> { grid, metadata, geom }, deriving each
    // template's placement geometry (entry/exit floor heights, fill columns,
    // eligibility bounds) up front. Procedural maps flatten their five
    // difficulty tiers (the tier becomes the `difficulty` label the generator
    // and DifficultyManager key off of); designed maps use a flat `scenes` dict
    // (the order is decided by `sequence`, not by difficulty).
    const processedTemplates = {};

    // [name, metadata.difficulty] pairs to process. Designed scenes share a
    // single "designed" label (only used for the debug dropdown; designed maps
    // bypass difficulty selection entirely).
    const entries = [];
    if (this.isDesigned) {
      for (const [name, templateData] of Object.entries(this.map.scenes)) {
        entries.push([name, templateData, "designed"]);
      }
    } else {
      const tierKeys = ["level1", "level2", "level3", "level4", "level5"];
      for (const tier of tierKeys) {
        const tierTemplates = this.map.tiers[tier] || {};
        for (const [name, templateData] of Object.entries(tierTemplates)) {
          entries.push([name, templateData, tier]);
        }
      }
    }

    for (const [name, templateData, difficulty] of entries) {
      const grid = this.parseLayout(templateData.layout);
      const metadata = { ...templateData.metadata, difficulty };
      const geom = deriveTemplateGeometry(grid, {
        sceneWidth: this.sceneWidth,
        metadata,
      });

      // Absolute-placement maps don't chain entry/exit floors, so an open edge
      // column is intentional (a pit straddling a seam), not a seam defect.
      if (!this.absolutePlacement && (geom.entryIsPit || geom.exitIsPit)) {
        console.warn(
          `LevelGenerator: template "${name}" has a pit at its ` +
            `${geom.entryIsPit ? "entry" : "exit"} edge column; sections may ` +
            `not chain cleanly.`
        );
      }

      processedTemplates[name] = { grid, metadata, geom };
    }

    return processedTemplates;
  }

  /**
   * Designed maps only: the next scene name in the authored sequence, or null
   * once the course is exhausted. Debug force-template/force-difficulty are
   * intentionally ignored here — a designed course must play in its authored
   * order (forcing a template would corrupt the layout).
   */
  selectNextSequenceTemplate() {
    if (this.sequenceIndex >= this.map.sequence.length) return null;
    return this.map.sequence[this.sequenceIndex];
  }

  /**
   * Parse a layout string into a grid (rows top-to-bottom). Templates are
   * bottom-anchored and may be shorter than the play area — their empty top rows
   * are omitted and extrapolated as empty at render time. Per-column fill control
   * is via `metadata.fillColumns`; otherwise it's derived from the bottom row.
   */
  parseLayout(layout) {
    return layout
      .trim()
      .split("\n")
      .map((line) => line.split(""));
  }

  selectNextTemplate(lastTemplate) {
    // If this is the first template, use flat ground
    if (!lastTemplate) {
      return "flat_ground";
    }

    // Debug overrides from the DebugManager: a specific forced template wins
    // outright; a forced difficulty tier pins the pool to one difficulty.
    let forcedDifficulty = null;
    if (this.debugManager) {
      const forcedTemplate = this.debugManager.getForceTemplate();
      if (forcedTemplate && this.templates[forcedTemplate]) {
        return forcedTemplate;
      }
      forcedDifficulty = this.debugManager.getForceDifficulty();
    }

    // Pick the difficulty tier: forced (debug batch testing) or the normal
    // distance-driven weighted selection.
    let difficulty;
    if (forcedDifficulty) {
      difficulty = forcedDifficulty;
    } else {
      // Update difficulty manager with current player distance
      this.difficultyManager.updateDifficulty(this.playerDistance);

      // Get the currently selected difficulty level
      difficulty = this.difficultyManager.selectDifficulty();

      // Update debug manager with current difficulty weights if available
      if (this.debugManager) {
        this.debugManager.updateDifficultyWeights(
          this.difficultyManager.getDifficultyWeights()
        );
      }
    }

    // Candidate pool: templates of the chosen tier that fit on-screen when
    // placed at the current cumulative height (excluding flat_ground, which is
    // the guaranteed fallback below).
    let eligible = this.eligibleTemplates(difficulty);

    // If the chosen tier has nothing that fits at this height, widen to every
    // tier so the run keeps flowing while difficulty self-corrects. When a
    // difficulty is being force-tested we keep the batch pure instead and let
    // flat_ground bridge the height (the fallback just below).
    if (eligible.length === 0 && !forcedDifficulty) {
      eligible = this.eligibleTemplates(null);
    }

    // Last resort: flat_ground is eligible across the whole valid height band,
    // so it always terminates the search (and gently returns terrain to flat).
    if (eligible.length === 0) {
      return "flat_ground";
    }

    // Gentle re-centering: bias toward templates that move H back toward the
    // target band, so terrain random-walks instead of drifting to an extreme.
    const biased = this.applyHeightBias(eligible);
    const pool = biased.length > 0 ? biased : eligible;

    return pool[Math.floor(Math.random() * pool.length)];
  }

  /**
   * Names of non-flat templates eligible at the current height. Pass a tier to
   * restrict to it, or null to consider every tier.
   */
  eligibleTemplates(difficulty) {
    const result = [];
    for (const [name, template] of Object.entries(this.templates)) {
      if (name === "flat_ground") continue;
      if (difficulty && template.metadata.difficulty !== difficulty) continue;
      if (isEligible(template.geom, this.currentHeight)) result.push(name);
    }
    return result;
  }

  /**
   * Prefer templates whose net rise nudges H back toward H_TARGET when it has
   * drifted out of the central band. Returns a (possibly empty) subset; callers
   * fall back to the full eligible set when it's empty.
   */
  applyHeightBias(names) {
    if (this.currentHeight > H_TARGET + 2) {
      // Too high — prefer descents/flats.
      return names.filter((n) => this.templates[n].geom.netRise <= 0);
    }
    if (this.currentHeight < H_TARGET - 2) {
      // Too low — prefer climbs/flats.
      return names.filter((n) => this.templates[n].geom.netRise >= 0);
    }
    return names; // In-band: no preference.
  }

  generateScene(startX, forcedTemplate = null) {
    // Use forced template if provided, otherwise select based on previous template
    let selectedTemplate;

    // Always honor the explicit forcedTemplate parameter first
    if (forcedTemplate) {
      selectedTemplate = forcedTemplate;
    } else {
      // Get the last template used
      const lastSceneIndex = this.generatedScenes.length - 1;
      const lastTemplate =
        lastSceneIndex >= 0
          ? this.generatedScenes[lastSceneIndex].template
          : null;

      // Select next template based on the last template's end height
      selectedTemplate = this.selectNextTemplate(lastTemplate);
    }

    const templateRecord = this.templates[selectedTemplate];
    const template = templateRecord.grid;
    const geom = templateRecord.geom;

    // Resolve the vertical offset so this scene's entry floor lines up with the
    // previous scene's exit floor (one continuous surface). Every cell at local
    // world-row y renders at y+dy.
    const entryH = this.currentHeight;
    // Absolute placement: scenes render at their authored position (dy=0), so a
    // painted continuous canvas reproduces exactly across 30-col seams.
    let dy = this.absolutePlacement ? 0 : computeDy(geom, entryH);

    // A forced (debug) template may not fit at the current height. Render it at
    // its authored position (dy=0) so it's fully visible, and note the seam.
    // Designed-course scenes are also passed via `forcedTemplate`, but they are
    // authored to chain at the running height, so this debug-only fallback must
    // not apply to them.
    if (forcedTemplate && !this.isDesigned && !isEligible(geom, entryH)) {
      console.warn(
        `LevelGenerator: forced template "${selectedTemplate}" doesn't fit at ` +
          `height ${entryH}; rendering at its authored position (dy=0).`
      );
      dy = 0;
    }
    const exitH = geom.exitFloorY + dy;

    console.log(
      `Generating scene '${selectedTemplate}' at x=${startX} (H ${entryH}→${exitH}, dy=${dy})`
    );

    // Calculate world coordinates for the scene
    const sceneStartX = startX;
    const sceneEndX = startX + this.sceneWidth * this.gridCellSize;

    // Track what we've generated for this scene
    const generatedEntities = {
      blocks: [],
      obstacles: [],
      lasers: [],
      projectileSpawners: [],
    };

    // Templates are bottom-anchored and may be shorter than the play area: the
    // last grid row is world cell 0, rows run upward, and world cells above the
    // grid are simply empty (the omitted/empty top rows are extrapolated here).
    const gridHeight = template.length;

    // Iterate through the visible play area and create entities
    for (let y = 0; y < this.sceneHeight; y++) {
      const gridRow = gridHeight - 1 - y; // which grid row maps to world cell y
      if (gridRow < 0) continue; // above the authored grid → empty

      for (let x = 0; x < this.sceneWidth; x++) {
        // Get the grid cell value, or default to empty if out of bounds
        const gridValue = template[gridRow]?.[x] || ".";

        // Calculate world position for this grid cell, shifted by the scene's
        // vertical offset so it chains onto the previous section.
        const worldX = sceneStartX + x * this.gridCellSize;
        const worldY = this.baseGroundY + (y + dy) * this.gridCellSize;

        // Skip anything shifted entirely below the visible band (can happen when
        // a high-authored template is placed at a low height, dy<0). The
        // fill-skirt below provides any needed foundation, so these off-screen
        // blocks would just be wasted.
        const renderedCell = y + dy;
        if (renderedCell < 0) continue;

        // Create the appropriate entity based on the grid value
        if (gridValue === "B") {
          // Regular 1-cell block. Solid foundation below the lowest blocks (and
          // below the screen) is added once per column by the fill-skirt pass.
          const block = this.blockManager.createBlock(
            worldX,
            worldY,
            this.gridCellSize,
            this.gridCellSize
          );
          generatedEntities.blocks.push(block);
        } else if (gridValue === "S") {
          // Create spike
          const spike = this.obstacleManager.createSpike(worldX, worldY);
          generatedEntities.obstacles.push(spike);
        } else if (gridValue === "L") {
          // Determine if this laser should have sparks by checking for a block below it
          let createSparks = false;

          // Check the cell directly below in the template grid
          const cellBelow =
            gridRow + 1 < gridHeight ? template[gridRow + 1][x] : null;

          // Create sparks if there's a block below this laser
          if (cellBelow === "B") {
            createSparks = true;
          }

          // A laser in the template's top authored row is a full-height beam: its
          // upper rows were collapsed away, so it must extend up off-screen. (A
          // laser embedded lower in the grid stays a normal one-cell beam.)
          const isTopRow = gridRow === 0;

          // Create laser (with sparks if needed)
          const laser = this.laserManager.createLaser(
            worldX,
            worldY,
            createSparks
          );

          // Top-row laser: extend both the beam visual AND its collision up past
          // the camera, so it gates the whole vertical column the way the old
          // stacked-L column did. The beam/glow geometry is shared across all
          // lasers, so we stretch this one via mesh.scale instead of allocating a
          // new geometry (keeping the bottom edge anchored).
          if (isTopRow && laser.beam) {
            const baseHeight = this.laserManager.beamHeight; // cell height (0.5)
            const extendedHeight = 30; // well beyond any camera frustum height
            const scaleY = extendedHeight / baseHeight;
            const shiftUp = (extendedHeight - baseHeight) / 2;

            laser.beam.scale.y = scaleY;
            laser.beam.position.y += shiftUp;

            if (laser.glow) {
              laser.glow.scale.y = scaleY;
              laser.glow.position.y += shiftUp;
            }

            // Extend collision up to above the play area (the player can never be
            // higher), so the gate can't be jumped through. Bottom edge unchanged.
            laser.bounds.top =
              this.baseGroundY + (this.sceneHeight + 2) * this.gridCellSize;
          }

          generatedEntities.lasers.push(laser);
        } else if (gridValue === "l") {
          // Horizontal laser: a thin beam running left-to-right across its cell,
          // pulsing on the same global phase as vertical lasers. Authors enclose
          // a row of `l` between two blocks so it reads as a contained gate.
          const laser = this.laserManager.createHorizontalLaser(worldX, worldY);
          generatedEntities.lasers.push(laser);
        } else if (gridValue === "p" && this.projectileManager) {
          // Horizontal projectile spawner: fires left toward the oncoming player
          // (needs a backstop block to its left — enforced at template load).
          const spawner = this.projectileManager.createSpawner(
            worldX,
            worldY,
            "left"
          );
          generatedEntities.projectileSpawners.push(spawner);
        } else if (gridValue === "P" && this.projectileManager) {
          // Vertical projectile spawner: fires straight down (mount it on a
          // ceiling/overhang so the player times passing underneath). Rounds
          // hit the floor or are despawned past the bottom of the play area.
          const spawner = this.projectileManager.createSpawner(
            worldX,
            worldY,
            "down"
          );
          generatedEntities.projectileSpawners.push(spawner);
        }
      }
    }

    // Fill-skirt pass: for each "fill" column, render one tall block from below
    // the screen up to the bottom of that column's lowest block, so foundations
    // read as solid all the way down when the section is raised. Non-fill
    // columns stay open (pits/chasms remain lethal at any height). This
    // generalizes the old bottom-row "extend down 3×" special case.
    const skirtBottomY = this.baseGroundY - 2; // below the fall-death line
    for (let x = 0; x < this.sceneWidth; x++) {
      if (!geom.fillColumns[x]) continue;

      const lowestBlockY = (geom.columnLowestBlockY[x] ?? 0) + dy;
      const skirtTopY = this.baseGroundY + lowestBlockY * this.gridCellSize;
      const skirtHeight = skirtTopY - skirtBottomY;
      if (skirtHeight <= 0) continue; // nothing below screen to fill

      const worldX = sceneStartX + x * this.gridCellSize;
      const block = this.blockManager.createBlock(
        worldX,
        skirtBottomY,
        this.gridCellSize,
        skirtHeight
      );
      generatedEntities.blocks.push(block);
    }

    // Advance the cumulative height so the next scene chains onto this one.
    this.currentHeight = exitH;

    // Create debug markers if debug manager is available
    const sceneMarkers = [];

    if (this.debugManager) {
      // Bottom left corner
      const bottomLeftMarker = this.debugManager.createMarker(
        sceneStartX,
        this.baseGroundY
      );

      // Top left corner
      const topLeftMarker = this.debugManager.createMarker(
        sceneStartX,
        this.baseGroundY + this.sceneHeight * this.gridCellSize
      );

      if (bottomLeftMarker && topLeftMarker) {
        sceneMarkers.push(bottomLeftMarker, topLeftMarker);
      }
    }

    // Store the generated scene
    this.generatedScenes.push({
      startX: sceneStartX,
      endX: sceneEndX,
      entities: generatedEntities,
      template: selectedTemplate,
      markers: sceneMarkers,
      metadata: this.templates[selectedTemplate].metadata,
      dy,
      entryH,
      exitH,
    });

    return generatedEntities;
  }

  /**
   * Designed maps only: place the next scene in the authored sequence at startX.
   * Returns false once the whole course has been placed (so callers stop). When
   * the finish scene lands, records the finish X and draws the finish line.
   */
  appendDesignedScene(startX) {
    const name = this.selectNextSequenceTemplate();
    if (!name) return false; // course fully placed — nothing left to generate
    this.generateScene(startX, name);
    this.sequenceIndex++;

    // Did we just place the terminal (finish) scene?
    if (this.sequenceIndex >= this.map.sequence.length) {
      const finishScene = this.generatedScenes[this.generatedScenes.length - 1];
      this.courseEndX = finishScene.endX;
      this.createFinishMarker(this.courseEndX);
    }
    return true;
  }

  update(playerX) {
    // Keep track of the player's distance for difficulty scaling
    this.playerDistance = playerX;

    // Check if we need to generate a new scene
    const lastScene = this.generatedScenes[this.generatedScenes.length - 1];

    // If there are no scenes yet, or the player is approaching the end of the last scene,
    // generate a new scene ahead. Designed courses walk their fixed sequence and
    // simply stop generating once it's exhausted (the finish scene is the last
    // thing on screen); procedural maps generate forever.
    if (!lastScene || playerX + 30 > lastScene.endX) {
      const startX = lastScene ? lastScene.endX : 0;
      if (this.isDesigned) {
        this.appendDesignedScene(startX);
      } else {
        this.generateScene(startX);
      }
    }

    // Update debug template info if debug manager is available
    if (this.debugManager) {
      this.debugManager.updateTemplateInfo(this.generatedScenes, playerX);
    }

    // --- Added Scene Cleanup Logic ---
    const cleanupDistance = 50; // Distance behind the player to cleanup scenes (in world units)
    const cleanupThresholdX = playerX - cleanupDistance;

    // Iterate backwards to safely remove elements
    for (let i = this.generatedScenes.length - 1; i >= 0; i--) {
      const scene = this.generatedScenes[i];

      // Check if the scene's end is completely behind the cleanup threshold
      if (scene.endX < cleanupThresholdX) {
        console.log(
          `Cleaning up scene: ${scene.template} ending at ${scene.endX}`
        );

        // Remove associated blocks using BlockManager
        if (scene.entities && scene.entities.blocks) {
          scene.entities.blocks.forEach((block) => {
            // Use the existing removeBlock which handles disposal
            this.blockManager.removeBlock(block);
          });
        }

        // Remove associated obstacles
        this.obstacleManager.removeObstaclesBeforeX(scene.startX);

        // Remove associated lasers
        this.laserManager.removeLasersBeforeX(scene.startX);

        // Remove associated projectile spawners and projectiles
        this.projectileManager.removeSpawnersBeforeX(scene.startX);
        this.projectileManager.removeProjectilesBeforeX(scene.startX);

        // Remove debug markers if they exist
        if (this.debugManager && scene.markers && scene.markers.length > 0) {
          this.debugManager.removeMarkers(scene.markers);
        }

        // Remove the scene from the tracked list
        this.generatedScenes.splice(i, 1);
      }
    }
    // --- End Added Scene Cleanup Logic ---
  }

  reset() {
    this.currentSceneX = 0;
    this.generatedScenes = [];

    // Tear down designed-course state (finish line + sequence cursor) so a
    // restart re-walks the course from the beginning with a fresh marker.
    this.sequenceIndex = 0;
    this.courseEndX = null;
    this.disposeFinishMarker();

    // Get initial player position from debug manager if available
    if (this.debugManager && this.debugManager.initialPlayerPosition > 0) {
      this.playerDistance = this.debugManager.initialPlayerPosition;
      this.currentSceneX = this.playerDistance;
    } else {
      this.playerDistance = 0;
    }

    // Reset the difficulty manager
    this.difficultyManager.reset();

    // Update difficulty based on player distance
    if (this.playerDistance > 0) {
      this.difficultyManager.updateDifficulty(this.playerDistance);
    }

    // Determine the start position for the first scene
    const startX = this.playerDistance > 0 ? this.playerDistance - 15 : -15;

    if (this.isDesigned) {
      // Designed course: start the continuous surface at the first scene's own
      // entry floor (so it renders at its authored shape, dy=0) and walk the
      // sequence in order. No forced flat_ground — the author controls the
      // opening (the example course opens on flat_ground itself).
      this.startX0 = startX;
      const firstScene = this.map.sequence[0];
      this.currentHeight = this.templates[firstScene].geom.entryFloorY;

      // Place a few scenes up front to fill the initial view (or the whole
      // course if it's shorter). update() appends the rest as the player nears
      // the end; appendDesignedScene draws the finish line when it lands.
      let nextStartX = startX;
      for (let i = 0; i < 3; i++) {
        if (!this.appendDesignedScene(nextStartX)) break;
        nextStartX = this.generatedScenes[this.generatedScenes.length - 1].endX;
      }
    } else {
      // Procedural: start the continuous surface at flat_ground's floor height
      // so the first (forced flat) scenes render at dy=0, identical to before.
      this.currentHeight = this.templates.flat_ground.geom.exitFloorY;

      // First two scenes: always flat ground to give the player time to react.
      this.generateScene(startX, "flat_ground");
      this.generateScene(
        startX + this.sceneWidth * this.gridCellSize,
        "flat_ground"
      );
      // Third scene: normal template selection.
      this.generateScene(startX + this.sceneWidth * this.gridCellSize * 2);
    }
  }

  /**
   * Designed maps: world X of the finish line (right edge of the finish scene),
   * or null until it's been placed. Procedural maps return null.
   */
  getFinishX() {
    return this.courseEndX;
  }

  /**
   * Designed maps: the course length in world units (finish X minus the start X
   * of the first scene). Invariant to the debug start offset, so the time-trial
   * score it feeds is consistent regardless of where the run was started.
   */
  getCourseLength() {
    return this.courseEndX == null ? 0 : this.courseEndX - this.startX0;
  }

  /**
   * Draw the finish-line marker at world X `xWorld`. A tall, thin striped post
   * spanning the play area. Owned by this generator and disposed in reset().
   */
  createFinishMarker(xWorld) {
    if (this.finishMesh) return; // already placed

    const height = this.sceneHeight * this.gridCellSize;
    const geometry = new THREE.PlaneGeometry(0.6, height);
    const material = new THREE.MeshBasicMaterial({
      color: 0xffffff,
      transparent: true,
      opacity: 0.85,
      side: THREE.DoubleSide,
    });
    const mesh = new THREE.Mesh(geometry, material);
    mesh.position.set(xWorld, this.baseGroundY + height / 2, 0.5);
    this.scene.add(mesh);
    this.finishMesh = mesh;
  }

  /** Remove and dispose the finish-line marker if present. */
  disposeFinishMarker() {
    if (!this.finishMesh) return;
    this.scene.remove(this.finishMesh);
    this.finishMesh.geometry.dispose();
    this.finishMesh.material.dispose();
    this.finishMesh = null;
  }

  /**
   * The segment names around a world position: the segment that contains it
   * (`current`, or "unknown" if none) and the one immediately before it
   * (`previous`, or null when there isn't one). Segments are contiguous and
   * stored left-to-right, so `previous` is the spatial left-neighbour. A
   * position at or past the last segment's right edge (a finished/over-run
   * course, which the half-open [startX, endX) test would otherwise miss)
   * reports that last segment as `current`, so completions still resolve a
   * segment and its predecessor.
   * @param {number} positionX - The X position to check
   * @returns {{ current: string, previous: string | null }}
   */
  getSceneContextAtPosition(positionX) {
    const scenes = this.generatedScenes;
    if (scenes.length === 0) return { current: "unknown", previous: null };

    let index = scenes.findIndex(
      (scene) => positionX >= scene.startX && positionX < scene.endX
    );
    if (index === -1 && positionX >= scenes[scenes.length - 1].endX) {
      index = scenes.length - 1; // at/past the finish line → the last segment
    }
    if (index === -1) return { current: "unknown", previous: null };

    return {
      current: scenes[index].template,
      previous: index > 0 ? scenes[index - 1].template : null,
    };
  }
}
